import React, { useEffect, useCallback, useMemo, useRef } from "react";
import { useDrawer } from "@/context/DrawerContext";
import { useAdvisorPABs } from "@/hooks/useDashboardStats";
import { AdvisorGoalsDrawer } from "@/pages/dashboard/components/AdvisorGoalsDrawer";
import { useAuth } from "@/hooks/useAuth";
import { hasRole } from "@/utils/roleUtils";

/**
 * Global AdvisorGoalsDrawer component that can be opened from anywhere in the app
 * Uses DrawerContext for state management and fetches PABs data
 * Also registers handlers in DrawerContext so the global Header buttons work
 */
export const UseAdvisorGoalsDrawer: React.FC = () => {
  const {
    isAdvisorGoalsDrawerOpen,
    advisorGoalsDrawerSelectedPabId,
    closeAdvisorGoalsDrawer,
    setAdvisorGoalsDrawerSelectedPabId,
    openAdvisorGoalsDrawer,
    setGoalsDrawerHandler,
    setMessagesDrawerHandler,
  } = useDrawer();
  const { user } = useAuth();
  const { data: pabs } = useAdvisorPABs({ enabled: hasRole(user, "advisor") });

  const hasPabs = Array.isArray(pabs) && pabs.length > 0;
  const handlersRegisteredRef = useRef(false);

  // Select first PAB if needed
  const selectFirstPabIfNeeded = useCallback(() => {
    if (!pabs || pabs.length === 0) return;
    if (!advisorGoalsDrawerSelectedPabId) {
      setAdvisorGoalsDrawerSelectedPabId(pabs[0].pabSessionId);
    }
  }, [pabs, advisorGoalsDrawerSelectedPabId, setAdvisorGoalsDrawerSelectedPabId]);

  // Create handler for goals only
  const openWithGoals = useCallback(
    (pabSessionId?: number) => {
      if (pabSessionId !== undefined) {
        setAdvisorGoalsDrawerSelectedPabId(pabSessionId);
      } else {
        selectFirstPabIfNeeded();
      }
      openAdvisorGoalsDrawer("goals", pabSessionId);
    },
    [selectFirstPabIfNeeded, openAdvisorGoalsDrawer, setAdvisorGoalsDrawerSelectedPabId],
  );

  // Register goals handler in DrawerContext so the Header goals button works for advisors.
  // Messages icon now opens MessagesListDrawer for advisors (no handler needed).
  useEffect(() => {
    if (!hasPabs) {
      handlersRegisteredRef.current = false;
      setGoalsDrawerHandler(null);
      setMessagesDrawerHandler(null);
      return;
    }

    if (handlersRegisteredRef.current) {
      return;
    }

    handlersRegisteredRef.current = true;
    setGoalsDrawerHandler(() => openWithGoals());
    // Deliberately not registering a messagesDrawerHandler so the header Messages icon
    // falls through to openMessagesListDrawer() for advisors.
  }, [
    hasPabs,
    openWithGoals,
    setGoalsDrawerHandler,
    setMessagesDrawerHandler,
  ]);

  // Ensure a valid PAB is selected when drawer opens
  // IMPORTANT: All hooks must be called before any conditional returns
  useEffect(() => {
    if (isAdvisorGoalsDrawerOpen && pabs && pabs.length > 0) {
      // If no PAB is selected or the selected PAB doesn't exist, select the first one
      if (
        !advisorGoalsDrawerSelectedPabId ||
        !pabs.find((p) => p.pabSessionId === advisorGoalsDrawerSelectedPabId)
      ) {
        setAdvisorGoalsDrawerSelectedPabId(pabs[0].pabSessionId);
      }
    }
  }, [
    isAdvisorGoalsDrawerOpen,
    pabs,
    advisorGoalsDrawerSelectedPabId,
    setAdvisorGoalsDrawerSelectedPabId,
  ]);

  // Find the selected PAB
  const selectedPab = useMemo(() => {
    if (!pabs || pabs.length === 0) {
      return undefined;
    }
    if (advisorGoalsDrawerSelectedPabId) {
      const found = pabs.find((p) => p.pabSessionId === advisorGoalsDrawerSelectedPabId);
      if (found) return found;
    }
    // Fallback to first PAB if no specific one is selected or found
    return pabs[0];
  }, [pabs, advisorGoalsDrawerSelectedPabId]);

  // Don't render anything if drawer is closed or user is not an advisor
  if (!isAdvisorGoalsDrawerOpen || !hasRole(user, "advisor")) {
    return null;
  }

  // If no PABs available, don't render the drawer
  if (!pabs || pabs.length === 0) {
    return null;
  }

  const handlePABChange = (pabSessionId: number) => {
    setAdvisorGoalsDrawerSelectedPabId(pabSessionId);
  };

  return (
    <AdvisorGoalsDrawer
      isOpen={isAdvisorGoalsDrawerOpen}
      onClose={closeAdvisorGoalsDrawer}
      pab={selectedPab}
      pabs={pabs}
      onPABChange={handlePABChange}
      initialTab="goals" // Always show goals tab
    />
  );
};
